/**
 * Report sul telefono (7/10/2026): risponde a "quanto abbiamo fatturato, e chi
 * ci fa guadagnare di più?".
 *
 * Prima, a 390px: "PERIODO SELEZIONATO" e una griglia di numeri (fatturato,
 * miglior divisione, top cliente col nome tagliato), poi "OPERATIVO · OGGI"
 * con tre zeri, poi due schede e il grafico in fondo, dopo tre schermate.
 * Qui:
 *   1. in grande, il fatturato del periodo; si tocca e si sceglie il periodo
 *      (gli stessi del computer, detti a parole: "luglio-settembre", non "Q3");
 *      sotto, il mese migliore;
 *   2. mese per mese: il grafico subito, con il numero solo sul mese migliore,
 *      e l'elenco dei mesi uno per uno a richiesta;
 *   3. chi ha fatturato di più (si apre la scheda del cliente) e le divisioni;
 *   4. da tenere d'occhio: progetti col margine basso, clienti in pausa,
 *      placement. Gli zeri non si mostrano: si dice "niente".
 * L'esportazione resta al computer, e lo dice una riga in fondo.
 * Stesse chiamate, stessa cache e stessi parametri nell'indirizzo del computer
 * (?period=…&year=…).
 */
import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { DIVISION_LABEL, type Division } from '@masamune/shared';
import { api } from '../../lib/api';
import { useAuthStore } from '../../stores/auth.store';
import { useUrlState } from '../../hooks/useUrlState';
import { fmtEur, fmtPct } from '../../lib/utils';
import { MEmpty, MGroup, MHero, MMore, MPage, MRow, MScelte, iniziali } from '../../components/mobile/MobileKit';
import { euro, nomeRiga } from '../admin/shared/AdminMobile';
import './report-mobile.css';

interface DivisionReport { division: Division; revenue: number; cost?: number; marginPct?: number }
interface ClientTop { clientId: string; legalName: string; revenue: number; marginPct?: number }
interface Operational { projectsBelowMargin: number | null; clientsAtRisk: number; placementYTD: number; placementsTotal: number }
interface Monthly { month: number; total: number; year?: number }

type PeriodKey = 'ytd' | 'q1' | 'q2' | 'q3' | 'q4' | 'year' | 'all';
interface Periodo { key: PeriodKey; year: number }
const CHIAVI: readonly PeriodKey[] = ['ytd', 'q1', 'q2', 'q3', 'q4', 'year', 'all'];

const MESI = ['gennaio', 'febbraio', 'marzo', 'aprile', 'maggio', 'giugno', 'luglio', 'agosto', 'settembre', 'ottobre', 'novembre', 'dicembre'];
const MESI3 = ['gen', 'feb', 'mar', 'apr', 'mag', 'giu', 'lug', 'ago', 'set', 'ott', 'nov', 'dic'];
const maiuscola = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

/** Le stesse scelte del computer (ReportPage, buildPeriodOptions), nello stesso ordine. */
function opzioniPeriodo(anno: number): Periodo[] {
  return [
    { key: 'ytd', year: anno }, { key: 'q1', year: anno }, { key: 'q2', year: anno }, { key: 'q3', year: anno }, { key: 'q4', year: anno },
    { key: 'year', year: anno - 1 }, { key: 'ytd', year: anno - 1 }, { key: 'all', year: anno },
  ];
}

/** Il periodo detto a parole: "da gennaio a oggi", "luglio-settembre", "tutto il 2025". */
function inParole(p: Periodo, annoOra: number): string {
  const anno = p.year !== annoOra ? ` ${p.year}` : '';
  switch (p.key) {
    case 'ytd': return p.year === annoOra ? 'da gennaio a oggi' : `il ${p.year}, da gennaio`;
    case 'q1': return `gennaio-marzo${anno}`;
    case 'q2': return `aprile-giugno${anno}`;
    case 'q3': return `luglio-settembre${anno}`;
    case 'q4': return `ottobre-dicembre${anno}`;
    case 'year': return `tutto il ${p.year}`;
    default: return 'tutto lo storico';
  }
}

/** Quali mesi appartengono al periodo (0-11): come mesiDelPeriodo del computer. */
function mesiDelPeriodo(p: Periodo): number[] {
  const tutti = Array.from({ length: 12 }, (_, i) => i);
  if (p.key === 'q1') return tutti.slice(0, 3);
  if (p.key === 'q2') return tutti.slice(3, 6);
  if (p.key === 'q3') return tutti.slice(6, 9);
  if (p.key === 'q4') return tutti.slice(9, 12);
  if (p.key === 'ytd') {
    const ora = new Date();
    return p.year === ora.getFullYear() ? tutti.slice(0, ora.getMonth() + 1) : tutti;
  }
  return tutti;
}

export function ReportMobile() {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const showMargin = !!user?.role.canSeeMargin;
  const annoOra = new Date().getFullYear();
  const [periodParam] = useUrlState<string>('period', 'ytd');
  const [yearParam] = useUrlState<string>('year', String(annoOra));
  const [, setParams] = useSearchParams();
  const periodo = useMemo<Periodo>(() => {
    const key = (CHIAVI as readonly string[]).includes(periodParam) ? (periodParam as PeriodKey) : 'ytd';
    const y = Number.parseInt(yearParam, 10);
    return { key, year: y >= 2000 && y <= 2100 ? y : annoOra };
  }, [periodParam, yearParam, annoOra]);
  const [scegliPeriodo, setScegliPeriodo] = useState(false);
  const [mesiUnoPerUno, setMesiUnoPerUno] = useState(false);

  const qs = `period=${periodo.key}&year=${periodo.year}`;
  const { data: divisioni } = useQuery({ queryKey: ['report', 'divs', periodo.key, periodo.year], queryFn: () => api.get<DivisionReport[]>(`/reports/divisions?${qs}`) });
  const { data: clienti } = useQuery({ queryKey: ['report', 'clients', periodo.key, periodo.year], queryFn: () => api.get<ClientTop[]>(`/reports/clients-top?${qs}`) });
  const { data: mensile } = useQuery({ queryKey: ['report', 'monthly', periodo.key, periodo.year], queryFn: () => api.get<Monthly[]>(`/reports/monthly?${qs}`) });
  const { data: op } = useQuery({ queryKey: ['report', 'op'], queryFn: () => api.get<Operational>('/reports/operational') });

  const scegli = (v: string) => {
    const [key, y] = v.split(':');
    setParams((prev) => {
      const sp = new URLSearchParams(prev);
      if (key === 'ytd') sp.delete('period'); else sp.set('period', key!);
      if (Number(y) === annoOra) sp.delete('year'); else sp.set('year', y!);
      return sp;
    });
    setScegliPeriodo(false);
  };

  const inclusi = new Set(mesiDelPeriodo(periodo));
  const mesi = Array.from({ length: 12 }, (_, i) => mensile?.find((m) => m.month === i + 1)?.total ?? 0);
  const fatturato = mesi.reduce((a, t, i) => (inclusi.has(i) ? a + t : a), 0);
  const annoGrafico = mensile?.[0]?.year ?? periodo.year;
  const meseOra = annoGrafico === annoOra ? new Date().getMonth() : -1;
  // Il mese migliore DENTRO il periodo: è l'unico che porta il suo numero sul grafico.
  const top = [...inclusi].reduce((best, i) => (mesi[i]! > (mesi[best] ?? -1) ? i : best), [...inclusi][0] ?? 0);
  const max = Math.max(1, ...mesi);
  const totaleClienti = (clienti ?? []).reduce((a, c) => a + (c.revenue ?? 0), 0) || 1;
  const parole = inParole(periodo, annoOra);

  const daGuardare: Array<{ key: string; titolo: string; sub: string; trail: string; to?: string }> = [];
  if (op?.projectsBelowMargin) daGuardare.push({ key: 'margine', titolo: 'Progetti col margine basso', sub: 'sotto la soglia di margine', trail: String(op.projectsBelowMargin), to: '/progetti' });
  if (op?.clientsAtRisk) daGuardare.push({ key: 'rischio', titolo: 'Clienti in pausa', sub: 'da sentire prima che se ne vadano', trail: String(op.clientsAtRisk) });
  if (op?.placementsTotal) daGuardare.push({ key: 'talent', titolo: 'Placement di Talent', sub: 'assunti, sul totale in corsa', trail: `${op.placementYTD} su ${op.placementsTotal}`, to: '/talent' });

  return (
    <MPage>
      <MHero
        label={`Fatturato, ${parole}`}
        unit="€"
        value={fmtEur(fatturato)}
        sub={periodo.key === 'all'
          ? `lo storico completo non c'è: qui c'è solo il ${annoGrafico}`
          : fatturato > 0 ? `il mese migliore è ${MESI[top]}: ${euro(mesi[top]!)}` : 'nessuna fattura in questo periodo'}
        onClick={() => setScegliPeriodo(true)}
      />

      <MGroup title={`Mese per mese, ${annoGrafico}`}>
        <div className="rpm-grafico" role="img"
          aria-label={`Fatturato mese per mese nel ${annoGrafico}: il mese migliore del periodo è ${MESI[top]} con ${euro(mesi[top]!)}. L'elenco dei mesi è qui sotto.`}>
          <div className="rpm-barre" aria-hidden="true">
            {mesi.map((t, i) => {
              const pct = (t / max) * 100;
              const cls = !inclusi.has(i) ? ' is-fuori' : i === top && t > 0 ? ' is-top' : '';
              return (
                <div key={i} className="rpm-col">
                  {i === top && t > 0 && inclusi.has(i) && (
                    <span className={`rpm-cifra${i <= 1 ? ' is-sx' : i >= 10 ? ' is-dx' : ''}`} style={{ bottom: `calc(${pct}% + 4px)` }}>{euro(t)}</span>
                  )}
                  <div className={`rpm-barra${cls}`} style={{ height: `${Math.max(pct, t > 0 ? 2 : 1.5)}%` }} />
                </div>
              );
            })}
          </div>
          <div className="rpm-mesi" aria-hidden="true">
            {MESI3.map((m, i) => <span key={m} className={i === meseOra ? 'is-ora' : undefined}>{m}</span>)}
          </div>
        </div>
        {mesiUnoPerUno && mesi.map((t, i) => (inclusi.has(i) || t > 0) && (
          <MRow key={MESI[i]} title={maiuscola(MESI[i]!)} sub={i === meseOra ? 'in corso' : !inclusi.has(i) ? 'fuori dal periodo' : undefined}
            trail={t > 0 ? euro(t) : <span className="amk-manca">niente</span>} trailTone={!inclusi.has(i) ? 'mute' : undefined} />
        ))}
        <MMore label={mesiUnoPerUno ? 'Nascondi i mesi' : 'Vedi i mesi uno per uno'} onClick={() => setMesiUnoPerUno((v) => !v)} />
      </MGroup>

      <MGroup title="Chi ha fatturato di più" count={clienti?.length || undefined}>
        {!clienti ? <MEmpty>Carico i clienti…</MEmpty>
          : clienti.length === 0 ? <MEmpty>Nessuna fattura emessa in questo periodo.</MEmpty>
          : clienti.map((c) => (
            <MRow
              key={c.clientId}
              text={iniziali(c.legalName)}
              round
              title={nomeRiga(c.legalName)}
              sub={`${Math.round(((c.revenue ?? 0) / totaleClienti) * 100)}% dei primi ${clienti.length}`}
              trail={euro(c.revenue)}
              trailSub={showMargin && c.marginPct != null ? `margine ${Math.round(c.marginPct)}%` : undefined}
              onClick={() => navigate(`/clients/${c.clientId}`)}
            />
          ))}
      </MGroup>

      {(divisioni?.length ?? 0) > 0 && (
        <MGroup title="Per divisione">
          {divisioni!.map((d) => (
            <MRow key={d.division} title={DIVISION_LABEL[d.division] ?? d.division}
              sub={d.marginPct != null ? `margine ${fmtPct(d.marginPct)}` : 'margine riservato'} trail={euro(d.revenue)} />
          ))}
        </MGroup>
      )}

      <MGroup title="Da tenere d'occhio, oggi" note="L'esportazione del report si fa dal computer.">
        {!op ? <MEmpty>Carico…</MEmpty>
          : daGuardare.length === 0 ? <MEmpty>Niente: nessun progetto col margine basso e nessun cliente in pausa.</MEmpty>
          : daGuardare.map((r) => (
            <MRow key={r.key} title={r.titolo} sub={r.sub} trail={r.trail} onClick={r.to ? () => navigate(r.to!) : undefined} />
          ))}
      </MGroup>

      <MScelte
        open={scegliPeriodo}
        onClose={() => setScegliPeriodo(false)}
        titolo="Periodo del report"
        opzioni={opzioniPeriodo(annoOra).map((p) => ({ v: `${p.key}:${p.year}`, l: maiuscola(inParole(p, annoOra)), sub: p.key === 'year' || p.key === 'all' || p.year !== annoOra ? undefined : String(p.year) }))}
        valore={`${periodo.key}:${periodo.year}`}
        onScegli={scegli}
      />
    </MPage>
  );
}
